<template>
  <transition name="modal-fade" @after-enter="onAfterEnter" @before-leave="onBeforeLeave">
    <div v-if="visible" class="page-modal-overlay" @click.self="handleClose">
      <div class="page-modal-container">
        <div class="page-modal-header">
          <h2 class="page-modal-title">{{ title }}</h2>
          <button class="page-modal-close" @click="handleClose" :aria-label="$t('common.close')">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="page-modal-body">
          <div v-if="loading" class="page-modal-loading">
            <span class="loading-spinner"></span>
          </div>
          <div v-else-if="content" class="page-modal-content" v-html="content"></div>
          <div v-else-if="$slots.default" class="page-modal-content"><slot></slot></div>
          <div v-else class="page-modal-empty">
            <p>{{ $t('common.no_content') }}</p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { homeApi } from '@/api/home'
import { getFullUrl } from '@/api/request'

export default {
  name: 'PageModal',
  props: {
    visible: { type: Boolean, default: false },
    pageKey: { type: String, default: '' },
    title: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      pageData: null
    }
  },
  computed: {
    locale() {
      return this.$i18n ? this.$i18n.locale : 'zh'
    },
    content() {
      if (!this.pageData) return ''
      const suffix = this.locale === 'en' ? '_en' : ''
      const raw = this.pageData[this.pageKey + suffix] || this.pageData[this.pageKey] || ''
      if (!raw) return ''
      return this.processRichText(raw)
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.fetchPage()
      }
    }
  },
  mounted() {
    this._onKeydown = (e) => { if (e.key === 'Escape' && this.visible) this.handleClose() }
    document.addEventListener('keydown', this._onKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onKeydown)
    document.body.style.overflow = ''
  },
  methods: {
    async fetchPage() {
      if (this.pageData || !this.pageKey) return
      this.loading = true
      try {
        const res = await homeApi.getPage()
        if (res.code === 1 && res.data) {
          this.pageData = res.data
        }
      } catch (e) {
        console.error('获取页面内容失败', e)
      } finally {
        this.loading = false
      }
    },
    processRichText(html) {
      return html
        .replace(/<img\s+([^>]*?)src=["'](?!https?:\/\/)([^"']+)["']/gi,
          (m, pre, src) => `<img ${pre}src="${getFullUrl(src)}"`)
        .replace(/<video\s+([^>]*?)src=["'](?!https?:\/\/)([^"']+)["']/gi,
          (m, pre, src) => `<video ${pre}src="${getFullUrl(src)}"`)
        .replace(/<source\s+([^>]*?)src=["'](?!https?:\/\/)([^"']+)["']/gi,
          (m, pre, src) => `<source ${pre}src="${getFullUrl(src)}"`)
        .replace(/poster=["'](?!https?:\/\/)([^"']+)["']/gi,
          (m, p) => `poster="${getFullUrl(p)}"`)
    },
    onAfterEnter() {
      document.body.style.overflow = 'hidden'
    },
    onBeforeLeave() {
      document.body.style.overflow = ''
    },
    handleClose() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.page-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  will-change: opacity;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .page-modal-container,
.modal-fade-leave-active .page-modal-container {
  transition: transform 0.25s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;

  .page-modal-container {
    transform: translateY(20px);
  }
}

.page-modal-container {
  background: #fff;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  will-change: transform;
}

.page-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.page-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.page-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;

  &:hover {
    color: #333;
  }
}

.page-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 28px;
  min-height: 200px;
}

.page-modal-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e8e8e8;
  border-top-color: #333;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.page-modal-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #999;
  font-size: 14px;
}

.page-modal-content {
  color: #333;
  font-size: 14px;
  line-height: 1.8;

  ::v-deep {
    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 12px 0;
    }

    video {
      max-width: 100%;
      border-radius: 4px;
      margin: 12px 0;
    }

    p {
      margin: 12px 0;
    }

    h1, h2, h3, h4, h5, h6 {
      margin: 20px 0 12px;
      font-weight: 600;
    }

    a {
      color: #c8a45e;
      text-decoration: underline;
    }

    ul, ol {
      padding-left: 24px;
      margin: 12px 0;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 12px 0;

      td, th {
        border: 1px solid #e8e8e8;
        padding: 8px 12px;
      }
    }
  }
}

@media (max-width: 768px) {
  .page-modal-container {
    width: 95%;
    max-height: 90vh;
  }

  .page-modal-header {
    padding: 16px 20px;
  }

  .page-modal-body {
    padding: 20px;
  }
}
</style>
